import React, { useEffect, useState, useCallback, useMemo, useContext, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Calendar, Clock, Pencil,
  Building2, ExternalLink,
} from 'lucide-react';

import { toast } from 'react-toastify';

import { Button, Card, Badge } from '../../ui-v2';
import {
  getResumenCampana, deleteCampana,
  cambiarEstadoCampana, contarParticipacionesCanceladasSistema,
  descargarCsvResumen, registrarIntervencion, deshacerIntervencion, getCandidatosIntervencion,
  reabrirCita,
} from '../api';
import RegistrarIntervencionModal from '../compartidos/RegistrarIntervencionModal';
import ModalCancelarCita from '../compartidos/ModalCancelarCita';
import { Modal } from '../../ui-v2.5';
import AccionesCampanaCancelada from '../campanas/AccionesCampanaCancelada';
import { UsuarioContext } from '../../../providers/personaContext';
import PanelLotes from '../lotes/PanelLotes';
import FiltrosResumen from '../lotes/FiltrosResumen';

import MiniDonut from '../resumen/MiniDonut';
import SeccionCitas from '../resumen/SeccionCitas';
import SeccionUsuarios from '../resumen/SeccionUsuarios';
import SeccionTecnicos from '../resumen/SeccionTecnicos';

/**
 * ResumenCampana — pantalla v4.
 *
 * Cambios respecto a v3:
 *   - Quitado max-w-screen-2xl → ahora ocupa todo el ancho disponible.
 *   - Header refactorizado: mini-header sticky con info crítica (título,
 *     estado, progreso, editar) que queda fijo al scrollear.
 *   - Los detalles (descripción, fechas, horario) se han movido DENTRO del
 *     Hero para evitar duplicar información entre header grande y mini-header.
 *
 * z-index sticky: navbar global es z-[1050] top-0 (~44px). Usamos top-14
 * (56px) que deja margen suficiente bajo el navbar y z-30.
 */

const ESTADO_CAMPANA_VARIANT = {
  Activa: 'success', Borrador: 'secondary', Pausada: 'warning',
  Completada: 'accent', Cancelada: 'destructive',
};

export default function ResumenCampana() {
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const id = parseInt(idParam, 10);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filtroEstado, setFiltroEstado] = useState('todas');
  const [filtroTecnico, setFiltroTecnico] = useState('');

  const [cancelTarget, setCancelTarget] = useState(null);

  // Lotes · filtros del resumen. Se aplican en el SERVIDOR (lista, métricas y KPI), con la misma
  // función que el CSV. Al cambiarlos se recarga sin vaciar la pantalla.
  const [filtros, setFiltros] = useState({});
  const [actualizando, setActualizando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [versionLotes, setVersionLotes] = useState(0);
  const hayDatos = useRef(false);
  // Intervenciones sin cita (24/10): a quién se registra, y a quién se le deshace.
  const [registrarDe, setRegistrarDe] = useState(null);
  const [deshacerDe, setDeshacerDe] = useState(null);
  const [deshaciendo, setDeshaciendo] = useState(false);
  // 3/11 · reabrir una cita realizada (gestión).
  const [reabrirDe, setReabrirDe] = useState(null);
  const [reabriendo, setReabriendo] = useState(false);
  const [altaAbierta, setAltaAbierta] = useState(false);

  const { persona, tienePermiso } = useContext(UsuarioContext);
  const puedeGestionar = persona?.rol === 'Super Administrador'
    || (typeof tienePermiso === 'function' && tienePermiso('intervenciones.campanas.manage'));

  const cargar = useCallback(async () => {
    setActualizando(true);
    if (!hayDatos.current) setError(null);
    try {
      const res = await getResumenCampana(id, filtros);
      setData(res);
      hayDatos.current = true;
    } catch (e) {
      console.error('[ResumenCampana]', e);
      const msg = e?.response?.status === 404
        ? 'La campaña no existe.'
        : (e?.response?.data?.error || 'No se pudo cargar el resumen.');
      // Con datos en pantalla, un fallo al filtrar no la tira: se avisa y se queda lo que había.
      if (hayDatos.current) toast.error(msg); else setError(msg);
    } finally { setLoading(false); setActualizando(false); }
  }, [id, filtros]);

  useEffect(() => { if (id) cargar(); }, [id, cargar]);

  async function exportarCsv() {
    setExportando(true);
    try {
      await descargarCsvResumen(id, filtros);
    } catch (e) {
      toast.error('No se pudo exportar el CSV.');
    } finally { setExportando(false); }
  }

  // 3/11 (B1) · el icono de cancelar abre el MISMO modal que el panel del técnico (por el usuario,
  // por sistemas o no asistió), que llama a POST /citas/:id/cancelacion.
  function pedirCancelar(cita) { setCancelTarget(cita); }

  const citasFiltradas = useMemo(() => {
    if (!data) return [];
    return data.citas.filter((c) => {
      if (filtroEstado === 'vivas' && !['reservada', 'confirmada'].includes(c.estado)) return false;
      if (filtroEstado === 'reservada' && c.estado !== 'reservada') return false;
      if (filtroEstado === 'confirmada' && c.estado !== 'confirmada') return false;
      if (filtroEstado === 'realizadas' && c.estado !== 'realizada') return false;
      if (filtroEstado === 'no_asistio' && c.estado !== 'no_asistio') return false;
      if (filtroEstado === 'canceladas' && !c.estado.startsWith('cancelada')) return false;
      if (filtroTecnico && String(c.tecnico.id) !== String(filtroTecnico)) return false;
      return true;
    });
  }, [data, filtroEstado, filtroTecnico]);

  if (loading) {
    return (
      <div className="px-6 py-16 text-center text-sm text-slate-500">
        <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
        Cargando resumen…
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-6 py-12 max-w-xl mx-auto">
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-red-700 text-sm">
          {error}
        </div>
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate('/citas-tecnicas/campanas')}>
            <ArrowLeft className="w-4 h-4" />
            Volver a la lista
          </Button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { campana, metricas, citas, usuarios, tecnicos } = data;

  async function confirmarDeshacer() {
    setDeshaciendo(true);
    try {
      await deshacerIntervencion(campana.id, deshacerDe.id);
      toast.success('Intervención deshecha: vuelve a como estaba.');
      setDeshacerDe(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se ha podido deshacer.');
    } finally {
      setDeshaciendo(false);
    }
  }
  async function confirmarReabrir() {
    setReabriendo(true);
    try {
      await reabrirCita(reabrirDe.id);
      toast.success('Cita reabierta: vuelve a reservada.');
      setReabrirDe(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se ha podido reabrir la cita.');
    } finally {
      setReabriendo(false);
    }
  }
  // La cita viva de la persona, si la tiene: el modal ofrece marcar ESA en vez de registrar otra.
  const citaVivaDe = (u) => (u && u.cita && ['reservada', 'confirmada'].includes(u.cita.estado) ? u.cita : null);
  const editable = ['Borrador', 'Activa', 'Pausada'].includes(campana.estado);

  const donutData = [
    { label: 'Completados',  value: metricas.usuarios_realizado,  color: '#003c66' },
    { label: 'Con cita',     value: metricas.usuarios_con_cita,   color: '#10b981' },
    { label: 'Pendientes',   value: metricas.usuarios_pendientes, color: '#fbbf24' },
    { label: 'Cancelados',   value: metricas.usuarios_cancelado,  color: '#cbd5e1' },
  ];

  return (
    <div className="min-h-full">
      {/* MINI-HEADER STICKY: la info crítica siempre visible */}
      <div className="sticky top-14 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="px-6 py-3 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => navigate('/citas-tecnicas/campanas')}
            title="Volver a la lista"
            className="w-8 h-8 grid place-items-center rounded-md hover:bg-slate-100 text-slate-500 shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>

          <h1 className="text-sm font-medium text-slate-900 truncate min-w-0 flex-1">
            {campana.titulo}
          </h1>

          <div className="flex items-center gap-2 shrink-0">
            <Badge variant={ESTADO_CAMPANA_VARIANT[campana.estado] || 'secondary'}>
              {campana.estado}
            </Badge>
            <div className="hidden sm:flex items-center gap-1.5 text-xs">
              <div className="w-20 h-1 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${metricas.progreso_pct}%` }}
                />
              </div>
              <span className="text-primary font-medium tabular-nums">
                {metricas.progreso_pct}%
              </span>
            </div>
            {editable && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/citas-tecnicas/campanas/${campana.id}/editar`)}
              >
                <Pencil className="w-3.5 h-3.5" />
                Editar
              </Button>
            )}
            {/* Fase 13 (Opción C) — solo se renderiza si la campaña es Cancelada */}
            <AccionesCampanaCancelada
              campana={campana}
              onEliminada={() => navigate('/citas-tecnicas/campanas')}
              onReactivada={(resp) => {
                let msg = 'Campaña reactivada.';
                if (resp?.participaciones_revividas > 0) {
                  msg += ` Se restauraron ${resp.participaciones_revividas} participaciones.`;
                }
                if (resp?.aviso) msg += ` ${resp.aviso}`;
                toast.success(msg);
                cargar();
              }}
              eliminarCampana={deleteCampana}
              cambiarEstadoCampana={cambiarEstadoCampana}
              contarParticipacionesCanceladasSistema={contarParticipacionesCanceladasSistema}
            />
          </div>
        </div>
      </div>

      {/* CONTENIDO — full width sin cap */}
      <div className="px-6 py-6">
        {/* Lotes y filtros (DS v2). Lo de debajo —progreso, métricas, citas, usuarios, técnicos—
            respeta los filtros; los lotes, no (es la vista de toda la campaña). */}
        <PanelLotes
          campana={campana}
          puedeGestionar={puedeGestionar}
          version={versionLotes}
          onCambio={() => { setVersionLotes((v) => v + 1); cargar(); }}
        />
        <FiltrosResumen
          ambito={campana.ambito}
          disponibles={data.filtros_disponibles}
          filtros={filtros}
          onChange={setFiltros}
          kpis={data.kpis}
          actualizando={actualizando}
          onExportar={exportarCsv}
          exportando={exportando}
        />

        {/* Usuarios, justo debajo de los filtros y sus KPI, a ancho completo (24/10): es donde se
            trabaja —buscar a alguien y registrar su intervención—. */}
        {puedeGestionar && ['Activa', 'Pausada'].includes(campana.estado) && (
          <div className="mb-2 flex justify-end">
            <Button size="sm" variant="outline" onClick={() => setAltaAbierta(true)}>
              Registrar intervención a alguien de fuera
            </Button>
          </div>
        )}
        <SeccionUsuarios
          usuarios={usuarios}
          puedeGestionar={puedeGestionar}
          onRegistrar={setRegistrarDe}
          onDeshacer={setDeshacerDe}
        />

        {/* HERO con detalles dentro */}
        <Card className="p-6 mb-4 border-l-4 border-l-primary">
          {/* Descripción + meta arriba del hero */}
          <div className="mb-5 pb-5 border-b border-slate-100">
            {campana.descripcion && (
              <p className="text-sm text-slate-700 leading-relaxed mb-3 max-w-3xl">
                {campana.descripcion}
              </p>
            )}
            <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                {campana.ambito === 'colaborador' ? (
                  <ExternalLink className="w-3.5 h-3.5" />
                ) : (
                  <Building2 className="w-3.5 h-3.5" />
                )}
                {campana.ambito === 'colaborador' ? 'Colaboradores' : 'Internos'}
              </span>
              <span className="text-slate-300">·</span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Hasta {formatFecha(campana.fecha_limite)}
              </span>
              <span className="text-slate-300">·</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {campana.horario_inicio}–{campana.horario_fin} · {campana.duracion_cita} min/cita
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">
                Progreso de la campaña
              </div>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="text-5xl font-medium text-primary tabular-nums leading-none">
                  {metricas.progreso_pct}%
                </span>
                <span className="text-sm text-slate-500">
                  {metricas.usuarios_realizado} de {metricas.usuarios_total} usuarios completados
                </span>
              </div>
              <div className="mt-4 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${metricas.progreso_pct}%` }}
                />
              </div>

              <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <BreakdownCard label="Completados" value={metricas.usuarios_realizado} color="primary" />
                <BreakdownCard label="Con cita" value={metricas.usuarios_con_cita} color="emerald" />
                <BreakdownCard label="Pendientes" value={metricas.usuarios_pendientes} color="amber" />
                <BreakdownCard label="Cancelados" value={metricas.usuarios_cancelado} color="slate" />
              </div>
            </div>

            <div className="flex flex-col items-center justify-center">
              <MiniDonut
                data={donutData}
                size={150}
                centerLabel={`${metricas.progreso_pct}%`}
                centerSubLabel="Completado"
              />
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                {donutData.map((d) => (
                  <div key={d.label} className="flex items-center gap-1.5">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                      style={{ backgroundColor: d.color }}
                    />
                    <span className="text-slate-600">{d.label}</span>
                    <span className="text-slate-400 tabular-nums ml-auto">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Métricas de citas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <MetricaCard label="Citas reservadas" value={metricas.citas_reservadas + metricas.citas_confirmadas} hint={metricas.citas_confirmadas > 0 ? `${metricas.citas_confirmadas} confirmadas` : null} />
          <MetricaCard label="Realizadas" value={metricas.citas_realizadas} colorVar="primary" />
          <MetricaCard label="No asistió" value={metricas.citas_no_asistio} colorVar="warning" />
          <MetricaCard label="Canceladas" value={metricas.citas_canceladas} colorVar="muted" />
          {/* Fase 21 · #22 — métricas acumuladas/derivadas */}
          <MetricaCard
            label="Cancelaciones acumuladas"
            value={metricas.cancelaciones_acumuladas ?? 0}
            hint="Histórico (incluye no-shows)"
            colorVar="muted"
          />
          <MetricaCard
            label="Tasa de no-show"
            value={(metricas.tasa_no_show === null || metricas.tasa_no_show === undefined) ? '—' : `${metricas.tasa_no_show}%`}
            hint={(metricas.tasa_no_show === null || metricas.tasa_no_show === undefined) ? 'Sin citas cerradas' : 'Sobre citas cerradas'}
            colorVar="warning"
          />
          {/* Fase 22 · #21 — recordatorios automáticos enviados */}
          <MetricaCard
            label="Recordatorios enviados"
            value={metricas.recordatorios_enviados_total ?? 0}
            hint={metricas.recordatorios_enviados_por_tipo
              ? `${metricas.recordatorios_enviados_por_tipo.RECORDATORIO_1 ?? 0} de 1º · ${metricas.recordatorios_enviados_por_tipo.RECORDATORIO_2 ?? 0} de 2º`
              : null}
            colorVar="muted"
          />
        </div>

        {/* Citas — los usuarios han subido debajo de los filtros (24/10) */}
        <div className="mb-6">
          <SeccionCitas
            citas={citasFiltradas}
            totalCitas={citas.length}
            tecnicos={tecnicos}
            filtroEstado={filtroEstado}
            setFiltroEstado={setFiltroEstado}
            filtroTecnico={filtroTecnico}
            setFiltroTecnico={setFiltroTecnico}
            onCancelar={pedirCancelar}
            onReabrir={puedeGestionar ? setReabrirDe : null}
          />
        </div>

        {/* Técnicos full width */}
        <SeccionTecnicos tecnicos={tecnicos} />
      </div>

      <RegistrarIntervencionModal
        open={!!registrarDe}
        subtareas={data.subtareas || []}
        onClose={() => setRegistrarDe(null)}
        persona={registrarDe ? { id: registrarDe.id, nombre: registrarDe.nombre_completo } : null}
        tecnicos={tecnicos}
        citaViva={citaVivaDe(registrarDe)}
        registrar={(datos, pid) => registrarIntervencion(campana.id, pid, datos)}
        onHecho={() => { toast.success('Intervención registrada.'); cargar(); }}
      />
      {/* Alta: alguien que aún no está en la campaña (se añade a un lote y se registra, todo junto). */}
      <RegistrarIntervencionModal
        open={altaAbierta}
        subtareas={data.subtareas || []}
        onClose={() => setAltaAbierta(false)}
        tecnicos={tecnicos}
        lotes={data.filtros_disponibles?.lotes || []}
        buscarCandidatos={(q) => getCandidatosIntervencion(campana.id, q)}
        registrar={(datos, pid) => registrarIntervencion(campana.id, pid, datos)}
        onHecho={() => { toast.success('Añadida a la campaña y con la intervención registrada.'); setVersionLotes((v) => v + 1); cargar(); }}
      />
      <Modal
        open={!!deshacerDe}
        onClose={deshaciendo ? undefined : () => setDeshacerDe(null)}
        size="sm"
        tone="danger"
        title="Deshacer la intervención"
        subtitle={deshacerDe?.nombre_completo || ''}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDeshacerDe(null)} disabled={deshaciendo}>Cancelar</Button>
            <Button variant="destructive" onClick={confirmarDeshacer} disabled={deshaciendo}>
              {deshaciendo ? 'Deshaciendo…' : 'Deshacer'}
            </Button>
          </>
        )}
      >
        <p className="text-sm text-slate-700">
          Vuelve a como estaba antes de registrarla: si se registró sin cita, se borra; si se marcó su
          cita, la cita vuelve a estar reservada. Queda en el historial quién lo deshizo y cuándo.
        </p>
      </Modal>
      <Modal
        open={!!reabrirDe}
        onClose={reabriendo ? undefined : () => setReabrirDe(null)}
        size="sm"
        title="Reabrir la cita"
        subtitle={reabrirDe?.usuario?.nombre_completo || ''}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setReabrirDe(null)} disabled={reabriendo}>Cancelar</Button>
            <Button onClick={confirmarReabrir} disabled={reabriendo}>{reabriendo ? 'Reabriendo…' : 'Reabrir'}</Button>
          </>
        )}
      >
        <p className="text-sm text-slate-700">
          Vuelve a reservada y la persona, a «cita reservada». Las subtareas marcadas se conservan y
          queda en el historial quién la reabrió. Si la cita aún no ha pasado, se le vuelven a programar
          los recordatorios.
        </p>
      </Modal>

      <ModalCancelarCita
        cita={cancelTarget}
        persona={cancelTarget?.usuario?.nombre_completo}
        onClose={() => setCancelTarget(null)}
        onHecho={async () => {
          setCancelTarget(null);
          toast.success('Cita cancelada. Se ha avisado a la persona para que reserve otra.');
          await cargar();
        }}
      />
    </div>
  );
}

function MetricaCard({ label, value, hint, colorVar }) {
  const colorClass =
    colorVar === 'primary' ? 'text-primary' :
    colorVar === 'warning' ? 'text-amber-600' :
    colorVar === 'muted' ? 'text-slate-500' :
    'text-slate-900';
  return (
    <Card className="p-4">
      <div className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className={`text-3xl font-medium mt-1.5 tabular-nums ${colorClass} leading-none`}>
        {value}
      </div>
      {hint && (
        <div className="text-[11px] text-slate-400 mt-1.5">{hint}</div>
      )}
    </Card>
  );
}

function BreakdownCard({ label, value, color }) {
  const colorClass =
    color === 'primary' ? 'text-primary' :
    color === 'emerald' ? 'text-emerald-600' :
    color === 'amber' ? 'text-amber-600' :
    'text-slate-500';
  return (
    <div>
      <div className={`text-xl font-medium tabular-nums ${colorClass} leading-none`}>{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wide mt-1">{label}</div>
    </div>
  );
}

function formatFecha(s) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-ES', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}
